/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { Button, Icon } from 'semantic-ui-react'
import React, { useRef, useState, useEffect, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { convertOrderItemsToFulfillmentIssues } from '../../../../../../services/Order/Order.service'
import { useSaveFulfillment } from '../useSaveFulfillment'
import { debounce } from 'lodash'
import { useNavigate } from 'react-router-dom-v5-compat'
import useMediaQuery from 'beautiful-react-hooks/useMediaQuery'
import { MOBILE_THRESHOLD } from '../../../../../../constants'
import { QuantityUpdater } from './QuantityUpdater'
import { AdjustWeight } from '../AdjustWeight'
import { UltyModalWrapperContext } from '../../../../../Shared/UltyModalWrapper/UltyModalWrapperContext'
import { useScheduledOrderPrecheck } from '../useScheduledOrderPrecheck'

const OrderItemFulfillmentIssueBar = ({
                                          order,
                                          orderItem,
                                          hasReplacement,
                                          ...props
                                      }) => {
    const [t] = useTranslation()
    const isMobile = useMediaQuery(`(max-width: ${MOBILE_THRESHOLD}px)`);
    const [quantity, setQuantity] = useState(orderItem.quantity)
    const [displayOrderItemFulfillmentIssueBar, setDisplayOrderItemFulfillmentIssueBar] = useState(false)
    const {handleUltyModalWrapper} = useContext(UltyModalWrapperContext)
    const {saveFulfillmentIssuesMutation, isPendingFulfillment} = useSaveFulfillment(order.id, orderItem.id)
    const {withScheduledOrderPrecheck, needsPrecheck} = useScheduledOrderPrecheck(order)
    const navigate = useNavigate()

    const showAdjustWeightModal = () => {
        const modalSettings = {
            size: 'tiny',
            component: <AdjustWeight
              order={order}
              platformModule={order.platform.module}
              orderItem={orderItem}
              pickingMethod='MANUAL'
              onSubmit={() => {
                  handleUltyModalWrapper(false, null)
              }}
            />,
        }

        handleUltyModalWrapper(true, modalSettings)
    }

    const debouncedChangeOrderItemQuantity = useRef(debounce((newQuantity) => {
          if (newQuantity !== orderItem.quantity) {
              saveFulfillmentIssuesMutation.mutate({
                  fulfillmentIssues: convertOrderItemsToFulfillmentIssues(
                    [{...orderItem, quantity: newQuantity}],
                    [],
                  ),
              })
          }
      }, 2000),
    )

    const handleQuantityChange = (newQuantity) => {
        if (newQuantity === orderItem.quantity) {
            debouncedChangeOrderItemQuantity.current.cancel()
            return
        }

        if (needsPrecheck()) {
            withScheduledOrderPrecheck(() => {
                setQuantity(newQuantity)
                debouncedChangeOrderItemQuantity.current(newQuantity)
            })()
        } else {
            setQuantity(newQuantity)
            debouncedChangeOrderItemQuantity.current(newQuantity)
        }
    }

    useEffect(() => {
        setQuantity(orderItem.quantity);
    }, [orderItem.quantity])

    if (!displayOrderItemFulfillmentIssueBar) {
        return (
          <Button
            icon
            color="blue"
            className={props.className}
            labelPosition="right"
            onClick={() => setDisplayOrderItemFulfillmentIssueBar(true)}
          >
              {t('order.fulfillment_issue.fulfillment_issue')}
              <Icon name="arrow right"/>
          </Button>
        )
    }

    return (
      <div
        className={props.className}
        css={css`
            display: flex;
            justify-content: space-between;
        `}>
          <Button
            icon
            color="blue"
            onClick={() => setDisplayOrderItemFulfillmentIssueBar(false)}
          >
              <Icon name="arrow left"/>
          </Button>

          <div
            css={css`
                display: flex;
                justify-content: space-between;
                width: 100%;
                
                &  > :first-child {
                    margin-right: auto;
                }
            `}
          >

              {(!orderItem.fulfillmentAction || orderItem.fulfillmentAction === 'CANCEL_ORDER') && (<Button
                  color="red"
                  title={`${t('order.fulfillment_action.shopper_preference')} : ${t('order.fulfillment_action.CANCEL_ORDER')}`}
                  fluid
                  disabled={quantity === 0}
                  loading={isPendingFulfillment}
                  onClick={() => handleQuantityChange(0)}>
                    {t('order.fulfillment_action.CANCEL_ORDER_btn_label')}
                </Button>
              )}
              {(orderItem.fulfillmentAction && orderItem.fulfillmentAction !== 'CANCEL_ORDER') && (
                <>
                  {( !orderItem.item || !orderItem.item.pricedByUnit || orderItem.item.pricedByUnit.type === 'COUNT') ? (
                                  <QuantityUpdater
                                    order={order}
                                    orderItem={orderItem}
                                    hasReplacement={hasReplacement}
                                    onSubmit={() => {
                                        setDisplayOrderItemFulfillmentIssueBar(false)
                                    }}
                                  />
                                ) : (
                                  <Button
                                    css={css`
                                        &.ui.button {
                                              margin-right: 0.2rem;
                                        }
                                    `}
                                    onClick={showAdjustWeightModal}
                                  >
                                      {t('order.pickup.adjust_weight.adjust_weight')}
                                  </Button>
                  )}

                  <Button
                    color="red"
                    icon
                    disabled={quantity === 0}
                    loading={isPendingFulfillment}
                    onClick={() => handleQuantityChange(0)}>
                      <Icon name="trash"/>
                  </Button>

                  {order.platform.supportsSubstitution &&
                    orderItem.object &&
                    (
                    <Button
                      color="yellow"
                      icon
                      disabled={!orderItem.isReplaceable}
                      loading={isPendingFulfillment}
                      onClick={withScheduledOrderPrecheck(() => {
                          navigate(`items/${orderItem.id}/replacement?initialPickingMethod=MANUAL&hideNavBar=${isMobile}`)
                      })}>
                        <Icon name="retweet"/>
                    </Button>
                  )}
                </>
              )}

          </div>
      </div>
    )
}

export { OrderItemFulfillmentIssueBar }
